import { ApplicationRef, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { map } from 'rxjs/operators';

/**
 * Applies PrimeNG global `translation` (primelocale) so DatePicker, Calendar, filters, etc.
 * use the correct language without per-component `locale` inputs.
 * Reloads when ngx-translate language changes.
 */
@Injectable({ providedIn: 'root' })
export class PrimeNgI18nService {
    constructor(
        private readonly primeNG: PrimeNG,
        private readonly translate: TranslateService,
        private readonly appRef: ApplicationRef,
        private readonly http: HttpClient,
    ) {
        this.applyPrimeLocale();
        this.translate.onLangChange.subscribe(() => this.applyPrimeLocale());
    }

    private resolveLangCode(): string {
        const raw =
            this.translate.currentLang ||
            localStorage.getItem('lang') ||
            this.translate.getDefaultLang() ||
            'en';
        return raw.toLowerCase();
    }

    private unwrapLocaleRoot(root: Record<string, unknown>): Record<string, unknown> | null {
        const inner = root['en'] ?? root['fr'] ?? Object.values(root)[0];
        return inner && typeof inner === 'object' ? (inner as Record<string, unknown>) : null;
    }

    private applyPrimeLocale(): void {
        const lang = this.resolveLangCode();
        const url = lang.startsWith('fr')
            ? './assets/primelocale/primeng-fr.json'
            : './assets/primelocale/primeng-en.json';

        this.http
            .get<Record<string, unknown>>(url)
            .pipe(map((root) => this.unwrapLocaleRoot(root)))
            .subscribe({
                next: (locale) => {
                    if (!locale) {
                        this.fallbackEnglishLocale();
                        return;
                    }
                    this.primeNG.setTranslation(locale as any);
                    this.appRef.tick();
                },
                error: () => this.fallbackEnglishLocale(),
            });
    }

    private fallbackEnglishLocale(): void {
        this.http
            .get<Record<string, unknown>>('./assets/primelocale/primeng-en.json')
            .pipe(map((root) => this.unwrapLocaleRoot(root)))
            .subscribe((locale) => {
                if (locale) {
                    this.primeNG.setTranslation(locale as any);
                    this.appRef.tick();
                }
            });
    }
}
